import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useSiteChrome } from '../../context/SiteChromeContext';
import { pickSocialIcon, normaliseSocialHref } from '../../utils/socialIcons';

const navLinks = [
  { to: '/', label: 'Home' },
  {
    label: 'About NIS Abuja',
    children: [
      { to: '/about', label: 'About Us' },
      { to: '/mission-vision', label: 'Mission and Vision' },
      { to: '/constitution', label: 'Constitution' },
      { to: '/executive-members', label: 'Executive Members' },
      { to: '/subgroup', label: 'Subgroup' },
    ],
  },
  { to: '/publications', label: 'Publications' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/find-surveyor', label: 'Find a Surveyor' },
  { to: '/update-profile', label: 'Update Profile' },
  { to: '/contact', label: 'Contact' },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { settings: siteSettings } = useSiteSettings();
  const { getHeader, loaded: chromeLoaded } = useSiteChrome();
  const titleWidget = getHeader('Site Title');
  const titleText = (titleWidget?.data?.text || 'Nigerian Institution of Surveyors').trim();
  // Mobile shows "FCT Branch — Abuja" on a second line, so strip any
  // "- FCT Branch" / "— FCT Branch" suffix from the main title to avoid duplication.
  const mobileTitleText = titleText.replace(/\s*[-–—]\s*FCT\s*Branch.*$/i, '').trim();
  const ctaText = 'Join NIS FCT';
  // Strip outer <p>...</p> wrappers and HTML tags to get plain text from RTE widgets.
  const stripHtml = (html) => {
    if (!html) return '';
    const txt = String(html).replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
    return txt;
  };
  // The top utility bar can be driven by either of two widget shapes:
  //   - "Top Bar Contact": a single combined HTML widget (legacy).
  //   - "Contact Email" + "Contact Phone": granular widgets.
  // Whichever set was most recently edited wins, so the admin's latest save
  // is always what shows on the public Navbar.
  const ts = (w) => (w ? Date.parse(w.updated_at || '') || 0 : 0);
  const emailW = getHeader('Contact Email');
  const phoneW = getHeader('Contact Phone');
  const topBarW = getHeader('Top Bar Contact');
  const headerEmail = stripHtml(emailW?.data?.html);
  const headerPhone = stripHtml(phoneW?.data?.html);
  const topBarHtml = topBarW?.data?.html || '';
  const granularLatest = Math.max(ts(emailW), ts(phoneW));
  const useTopBar = topBarHtml && ts(topBarW) >= granularLatest;
  const topBarContactHtml = useTopBar ? topBarHtml : '';
  // Social Links: editable via the page editor ("Social Links" cards widget on
  // site_header). Each card -> { title: platform name, description: URL }.
  // Falls back to a sensible default set if the widget hasn't been seeded yet.
  const headerSocialItems = (getHeader('Social Links')?.data?.items || [])
    .map(it => ({ label: (it.title || '').trim(), href: normaliseSocialHref(it.description) }))
    .filter(it => it.label && it.href);
  const socialItems = headerSocialItems.length ? headerSocialItems : [
    { label: 'Facebook',  href: 'https://facebook.com/' },
    { label: 'Twitter',   href: 'https://twitter.com/' },
    { label: 'LinkedIn',  href: 'https://linkedin.com/' },
  ];
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setDropdownOpen(false); setMobileAboutOpen(false); }, [location.pathname]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => { logout(); navigate('/'); };

  const isAboutActive = navLinks.find(l => l.children)?.children.some(c => location.pathname === c.to);

  return (
    <header className={`sticky top-0 z-50 transition-shadow duration-300 ${scrolled ? 'shadow-lg' : ''}`}>
      {/* Top Utility Bar */}
      <div className="bg-[#062E10] text-white/80">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between h-9 text-xs">
          <div className="hidden sm:flex items-center gap-4 min-w-0 min-h-[1rem]">
            {/* Reserve space silently until the SiteChrome data has loaded so
                the admin's saved contact info doesn't briefly flash a stale
                hardcoded value. After load, render whatever the admin set;
                an empty widget renders as empty (no synthetic fallback). */}
            {chromeLoaded && (topBarContactHtml ? (
              <div
                className="prose prose-invert prose-xs max-w-none [&_p]:m-0 [&_a]:text-white/80 [&_a:hover]:text-white truncate"
                dangerouslySetInnerHTML={{ __html: topBarContactHtml }}
              />
            ) : (
              <>
                {headerEmail && (
                  <span className="flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                    {headerEmail}
                  </span>
                )}
                {headerPhone && (
                  <span className="flex items-center gap-1">
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                    {headerPhone}
                  </span>
                )}
              </>
            ))}
          </div>
          <div className="flex items-center gap-3 ml-auto">
            {/* Social icons — editable via the page editor (Site Header → Social Links) */}
            {socialItems.map((s) => (
              <a
                key={`${s.label}-${s.href}`}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition"
                aria-label={s.label}
                title={s.label}
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <path d={pickSocialIcon(s.label)} />
                </svg>
              </a>
            ))}
            <span className="border-l border-white/20 h-4 mx-1"></span>
            {user ? (
              <Link to={user.role === 'admin' ? '/admin' : '/portal'} className="hover:text-white transition font-medium">
                My Portal
              </Link>
            ) : (
              <>
                <Link to="/login" className="hover:text-white transition">Member Login</Link>
                <span className="text-white/30">|</span>
                <Link to="/register" className="hover:text-white transition">Register</Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`bg-[#0B5D1E] transition-all duration-300 ${scrolled ? 'bg-[#0B5D1E]/98 backdrop-blur-sm' : ''}`}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="relative flex justify-between items-center h-[72px]">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 flex-shrink-0">
              <img
                src={siteSettings?.header_logo || '/NIS ABJ LOGO.jpg'}
                alt="NIS FCT Branch"
                className="h-12 w-auto shadow-md object-contain"
              />
              <div className="hidden sm:block">
                <div className="text-white font-bold text-base leading-tight tracking-wide">
                  {mobileTitleText}
                </div>
                <div className="text-[#C8A951] text-xs font-semibold tracking-widest uppercase">
                  FCT Branch — Abuja
                </div>
              </div>
            </Link>

            {/* Mobile centered title (between logo and menu button) */}
            <Link
              to="/"
              className="lg:hidden sm:hidden absolute left-1/2 -translate-x-1/2 text-center px-1 max-w-[60%]"
              aria-label={mobileTitleText}
            >
              <div className="text-white font-bold text-[10px] leading-[1.1] tracking-tight whitespace-normal break-words">
                {mobileTitleText}
              </div>
              <div className="text-[#C8A951] text-[8px] font-semibold tracking-widest uppercase mt-0.5">
                FCT Branch — Abuja
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) =>
                link.children ? (
                  <div key={link.label} className="relative" ref={dropdownRef}>
                    <button
                      onClick={() => setDropdownOpen(!dropdownOpen)}
                      className={`relative px-4 py-2 text-sm font-semibold tracking-wide whitespace-nowrap transition-all duration-200 rounded flex items-center gap-1 ${
                        isAboutActive
                          ? 'text-[#C8A951] bg-white/10'
                          : 'text-white/90 hover:text-white hover:bg-white/8'
                      }`}
                    >
                      {link.label}
                      <svg className={`w-3.5 h-3.5 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                      </svg>
                      {isAboutActive && (
                        <span className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#C8A951] rounded-full"></span>
                      )}
                    </button>
                    {dropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
                        {link.children.map((child) => (
                          <Link
                            key={child.to}
                            to={child.to}
                            className={`block px-4 py-2.5 text-sm transition ${
                              location.pathname === child.to
                                ? 'text-[#0B5D1E] bg-[#0B5D1E]/5 font-semibold'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-[#0B5D1E]'
                            }`}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`relative px-4 py-2 text-sm font-semibold tracking-wide whitespace-nowrap transition-all duration-200 rounded ${
                      location.pathname === link.to
                        ? 'text-[#C8A951] bg-white/10'
                        : 'text-white/90 hover:text-white hover:bg-white/8'
                    }`}
                  >
                    {link.label}
                    {location.pathname === link.to && (
                      <span className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#C8A951] rounded-full"></span>
                    )}
                  </Link>
                )
              )}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-3">
              {user ? (
                <>
                  <Link
                    to={user.role === 'admin' ? '/admin' : '/portal'}
                    className="text-white/90 hover:text-white text-sm font-medium px-3 py-1.5 rounded hover:bg-white/10 transition"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="bg-[#C8A951] hover:bg-[#B89A42] text-[#062E10] px-5 py-2 rounded text-sm font-bold tracking-wide whitespace-nowrap transition shadow-md"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/register"
                  className="bg-[#C8A951] hover:bg-[#B89A42] text-[#062E10] px-6 py-2.5 rounded text-sm font-bold tracking-wide whitespace-nowrap transition shadow-md"
                >
                  {ctaText}
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden text-white p-2 rounded-lg hover:bg-white/10 transition"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div className={`lg:hidden overflow-hidden transition-all duration-300 ${mobileOpen ? 'max-h-[500px] border-t border-white/10' : 'max-h-0'}`}>
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label}>
                  <button
                    onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition ${
                      isAboutActive
                        ? 'bg-white/15 text-[#C8A951]'
                        : 'text-white/80 hover:bg-white/8 hover:text-white'
                    }`}
                  >
                    {link.label}
                    <svg className={`w-4 h-4 transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {mobileAboutOpen && (
                    <div className="ml-4 mt-1 space-y-1">
                      {link.children.map((child) => (
                        <Link
                          key={child.to}
                          to={child.to}
                          className={`block px-4 py-2.5 rounded-lg text-sm transition ${
                            location.pathname === child.to
                              ? 'bg-white/15 text-[#C8A951] font-medium'
                              : 'text-white/70 hover:bg-white/8 hover:text-white'
                          }`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition ${
                    location.pathname === link.to
                      ? 'bg-white/15 text-[#C8A951]'
                      : 'text-white/80 hover:bg-white/8 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
            <hr className="border-white/10 my-2" />
            {user ? (
              <>
                <Link to={user.role === 'admin' ? '/admin' : '/portal'} className="block px-4 py-3 rounded-lg text-white/80 hover:bg-white/8 text-sm font-medium">
                  Dashboard
                </Link>
                <button onClick={handleLogout} className="w-full text-left px-4 py-3 rounded-lg text-[#C8A951] hover:bg-white/8 text-sm font-medium">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="block px-4 py-3 rounded-lg text-white/80 hover:bg-white/8 text-sm font-medium">
                  Member Login
                </Link>
                <Link to="/register" className="block mx-4 mt-2 text-center bg-[#C8A951] text-[#062E10] py-2.5 rounded-lg text-sm font-bold">
                  {ctaText}
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
}
